/**
 * frontend/src/main.ts
 * =====================
 * Orquestador principal de la SPA.
 *
 * Responsabilidades:
 *   - Mantener el estado global (AppState)
 *   - Mantener las preferencias del usuario (Preferences, via localStorage)
 *   - Renderizar el header persistente con botón de preferencias
 *   - Mostrar el modal de onboarding la primera vez
 *   - Decidir qué vista renderizar según el estado
 *   - Pasar callbacks a las vistas para transiciones de estado
 *
 * Principio de independencia:
 *   main.ts es el único que conoce TODAS las vistas. Cada vista
 *   solo conoce su propia responsabilidad y comunica resultados via callbacks.
 *   Agregar una nueva vista = agregar un case en navigate() + crear el archivo.
 *
 * Máquina de estados:
 *   upload → procesando → esperando_aprobacion → procesando → completado
 *                                                            → cancelado
 *                      → error
 */

import type { AppState, EstadoConciliacion, Vista } from "./types";
import { renderUploadView }      from "./views/UploadView";
import { renderProcessingView }  from "./views/ProcessingView";
import { renderReviewView }      from "./views/ReviewView";
import { renderResultView }      from "./views/ResultView";
import { openPreferencesModal, type OpcionesPreferencias } from "./views/PreferencesView";
import { instalarTooltips }      from "./utils/ui";
import { showOnboardingModal, VERSION_TERMINOS } from "./components/OnboardingModal";
import { loadPreferences, savePreferences, usarPreferenciasDe } from "./utils/preferences";
import { renderLoginView }       from "./views/LoginView";
import { montarBotonDeReporte, desmontarBotonDeReporte } from "./components/ReporteFlotante";
import { montarBotonDeOpinion, desmontarBotonDeOpinion, registrarConciliacionCompletada } from "./components/OpinionFlotante";
import { detenerRecorrido, iniciarRecorrido, reiniciarRecorrido, usarRecorridoDe } from "./components/RecorridoGuiado";
import { obtenerResultado, consultarSesion, salir, alPerderLaSesion, consultarTerminos, aceptarTerminos } from "./api";
import { icon } from "./utils/icons";
// La versión en blanco, hecha para el fondo oscuro de la cabecera. Invertir
// la original con un filtro CSS dejaba los blancos amarillentos.
import logoUrl from "./assets/logo-negativo.png";

// ── Preferencias ──────────────────────────────────────────────────────────────

let preferences = loadPreferences();

// ── Estado global ─────────────────────────────────────────────────────────────

const state: AppState = {
  vista: "upload",
  threadId: null,
  resumen: null,
  errores: [],
  periodoLabel: "",
  driveUrlExcel: null,
  driveUrlPdf: null,
};

// ── Punto de montaje ─────────────────────────────────────────────────────────

const app = document.getElementById("app");
if (!app) throw new Error("Elemento #app no encontrado en el DOM.");

// Los tooltips se posicionan con JS porque el CSS no puede medir si arriba
// del icono queda lugar. Va una sola vez: el listener es delegado.
instalarTooltips();

// Una capa recortada al viewport mantiene las figuras fuera del flujo y del foco.
const backdropEl = document.createElement("div");
backdropEl.className = "brand-backdrop";
backdropEl.setAttribute("aria-hidden", "true");
backdropEl.innerHTML = `<span class="brand-backdrop__frame"></span><span class="brand-backdrop__circle"></span><span class="brand-backdrop__diamond"></span>`;
document.body.prepend(backdropEl);

// ── Header persistente ────────────────────────────────────────────────────────

const headerEl = document.createElement("header");
headerEl.className = "app-header";
headerEl.innerHTML = `
  <a class="app-header__brand" href="#app-content" aria-label="ConciliA, ir al inicio">
    <img class="app-header__logo" src="${logoUrl}" alt="ConciliA · Agente Conciliador Bancario" width="160" height="60" />
  </a>
  <div class="app-header__tools">
    <span class="app-header__usuario" id="app-usuario" hidden></span>
    <button class="app-header__ayuda btn" id="btn-recorrido" type="button" aria-label="Ver el recorrido guiado" data-tooltip="Recorrido guiado" hidden>
      ${icon("help")}
    </button>
    <button class="app-header__settings btn" id="btn-settings" title="Preferencias" type="button">
      ${icon("settings")} <span>Preferencias</span>
    </button>
    <button class="app-header__salir btn" id="btn-salir" type="button" hidden>
      ${icon("back")} <span>Salir</span>
    </button>
  </div>
`;

const contentEl = document.createElement("main");
contentEl.id = "app-content";
contentEl.className = "app-content";
contentEl.tabIndex = -1;
app.className = "app-shell";

app.appendChild(headerEl);
app.appendChild(contentEl);

/**
 * Lo que la vista en pantalla necesita rehacer cuando se guardan las
 * Preferencias, se abran desde donde se abran. Se limpia al cambiar de vista.
 */
let alCambiarPreferencias: (() => void) | null = null;

function abrirPreferencias(opciones: OpcionesPreferencias = {}, alGuardar?: () => void): void {
  openPreferencesModal(preferences, (updated) => {
    // Reasignación, no mutación: el modal devuelve un objeto nuevo. Por eso
    // las vistas reciben un getter (`() => preferences`) y no este objeto —
    // una vista que lo capture al renderizarse se queda con la versión vieja
    // hasta que se vuelva a montar, y con los archivos ya cargados eso no
    // pasa nunca. Era el bug de "guardo el umbral y la primera conciliación
    // lo ignora".
    preferences = updated;
    savePreferences(preferences);
    alGuardar?.();
    alCambiarPreferencias?.();
  }, opciones);
}

headerEl.querySelector("#btn-settings")!.addEventListener("click", () => abrirPreferencias());

headerEl.querySelector("#btn-recorrido")!.addEventListener("click", reiniciarRecorrido);

// ── Navegación ────────────────────────────────────────────────────────────────

function navigate(vista: Vista): void {
  alCambiarPreferencias = null;
  state.vista = vista;
  app!.dataset.vista = vista;
  contentEl.innerHTML = "";

  switch (vista) {
    case "upload":
      renderUploadView(contentEl, () => preferences, (threadId) => {
        state.threadId = threadId;
        navigate("procesando");
      }, (alGuardar) => abrirPreferencias({ enfocarDestinatarios: true }, alGuardar));
      break;

    case "procesando":
      renderProcessingView(contentEl, state, handleEstadoBackend);
      break;

    case "esperando_aprobacion":
      renderReviewView(contentEl, state, (decision) => {
        navigate(decision === "cancelar" ? "cancelado" : "enviando");
      }, {
        destinatarios: () => preferences.emailDestinatarios,
        abrirDestinatarios: (alGuardar) => abrirPreferencias({ enfocarDestinatarios: true }, alGuardar),
        alCambiarPreferencias: (redibujar) => { alCambiarPreferencias = redibujar; },
      });
      break;

    case "enviando":
      renderProcessingView(contentEl, state, handleEstadoBackend, true);
      break;

    case "completado":
    case "cancelado":
    case "error":
      renderResultView(contentEl, state, vista, volverAlInicio);
      break;
  }

  // La primera conciliación empieza acompañada. Después de los términos: el
  // recorrido señala la pantalla, y sobre la bienvenida no se vería.
  if (vista === "upload") iniciarRecorrido();
}

/**
 * Una conciliación nueva, desde la carga de archivos. La que estaba en curso
 * queda atrás: un aviso tardío del servidor sobre ella no hace volver a esa
 * pantalla, porque `ORIGENES_VALIDOS` no acepta saltos desde la carga.
 */
function volverAlInicio(): void {
  Object.assign(state, {
    vista: "upload",
    threadId: null,
    resumen: null,
    errores: [],
    periodoLabel: "",
    driveUrlExcel: null,
    driveUrlPdf: null,
  });
  navigate("upload");
}

// El logotipo lleva al inicio, como en cualquier aplicación. Sin sesión no
// hay inicio al que ir: se queda en la pantalla de ingreso.
headerEl.querySelector<HTMLAnchorElement>(".app-header__brand")!.addEventListener("click", (e) => {
  e.preventDefault();
  if (accesoRequerido && salirEl.hidden) return;
  volverAlInicio();
  contentEl.focus({ preventScroll: true });
  window.scrollTo({ top: 0, behavior: "smooth" });
});

// ── Manejador de cambio de estado del backend ────────────────────────────────

/**
 * Desde qué vistas es válido entrar a cada estado que reporta el backend.
 *
 * La máquina de estados avanza en un solo sentido. Un evento tardío —una
 * consulta de polling que resuelve después de que el usuario ya decidió— no
 * debe hacer retroceder la UI a una etapa superada: eso dejaba al usuario de
 * vuelta en la pantalla de revisión con el envío ya en curso.
 */
const ORIGENES_VALIDOS: Record<EstadoConciliacion, readonly Vista[]> = {
  procesando:           ["upload"],
  esperando_aprobacion: ["procesando"],
  completado:           ["procesando", "enviando"],
  cancelado:            ["procesando", "enviando", "esperando_aprobacion"],
  error:                ["procesando", "enviando", "esperando_aprobacion"],
};

async function handleEstadoBackend(
  status: EstadoConciliacion,
  resumen: string | null,
  errores: string[]
): Promise<void> {
  if (!ORIGENES_VALIDOS[status]?.includes(state.vista)) {
    // Evento fuera de secuencia: se descarta en vez de romper el flujo.
    return;
  }

  switch (status) {
    case "esperando_aprobacion":
      state.resumen = resumen;
      navigate("esperando_aprobacion");
      break;

    case "completado":
      if (state.threadId) {
        try {
          const resultado = await obtenerResultado(state.threadId);
          state.periodoLabel = resultado.periodo_label;
          state.driveUrlExcel = resultado.drive_url_excel;
          state.driveUrlPdf = resultado.drive_url_pdf;
        } catch {
          // Si falla la obtención del resultado, mostrar completado igual
        }
      }
      registrarConciliacionCompletada();
      navigate("completado");
      break;

    case "cancelado":
      navigate("cancelado");
      break;

    case "error":
      state.errores = errores;
      navigate("error");
      break;
  }
}

// ── Bootstrap ─────────────────────────────────────────────────────────────────

// ── Sesión ────────────────────────────────────────────────────────────────────

const usuarioEl = headerEl.querySelector<HTMLElement>("#app-usuario")!;
const salirEl   = headerEl.querySelector<HTMLButtonElement>("#btn-salir")!;
const ajustesEl = headerEl.querySelector<HTMLButtonElement>("#btn-settings")!;
const ayudaEl   = headerEl.querySelector<HTMLButtonElement>("#btn-recorrido")!;

/** Si esta instalación pide credenciales. Lo dice el servidor al arrancar. */
let accesoRequerido = false;

/**
 * Muestra quién está usando la aplicación.
 *
 * Con el usuario vacío la cabecera queda sin herramientas: en la pantalla de
 * ingreso no hay preferencias que ajustar ni sesión que cerrar.
 */
function mostrarUsuario(usuario: string): void {
  usuarioEl.innerHTML = usuario ? `${icon("shield")} <span>${usuario}</span>` : "";
  usuarioEl.hidden = !usuario;
  salirEl.hidden   = !usuario;
  ajustesEl.hidden = !usuario && accesoRequerido;
  ayudaEl.hidden   = ajustesEl.hidden;
}

/** Nombre legible de cada vista, para que el reporte diga dónde estaba. */
const NOMBRE_DE_VISTA: Record<Vista, string> = {
  upload: "Preparar fuentes",
  procesando: "Analizando movimientos",
  enviando: "Enviando el reporte",
  esperando_aprobacion: "Revisar y aprobar",
  completado: "Resultado",
  cancelado: "Reporte no enviado",
  error: "Error",
};

/** Deja la aplicación lista para trabajar con las preferencias de quien entró. */
async function empezar(usuario: string): Promise<void> {
  usarPreferenciasDe(usuario);
  usarRecorridoDe(usuario);
  preferences = loadPreferences();
  mostrarUsuario(usuario);
  montarBotonDeReporte(() => ({
    pantalla: NOMBRE_DE_VISTA[state.vista],
    threadId: state.threadId,
  }));
  montarBotonDeOpinion(usuario, () => NOMBRE_DE_VISTA[state.vista]);

  // Si la consulta falla se piden los términos igual: ante la duda, que el
  // usuario acepte de nuevo, y no que trabaje sin haberlos aceptado.
  const aceptados = await consultarTerminos(VERSION_TERMINOS)
    .then(t => t.aceptado)
    .catch(() => false);

  if (!aceptados) {
    showOnboardingModal(async () => {
      await aceptarTerminos(VERSION_TERMINOS);
      navigate("upload");
    });
  } else {
    navigate("upload");
  }
}

function pedirIngreso(): void {
  mostrarUsuario("");
  // Sin sesión no hay a dónde enviar un reporte, y el botón sobre la pantalla
  // de ingreso solo invita a un 401.
  desmontarBotonDeReporte();
  desmontarBotonDeOpinion();
  detenerRecorrido();
  renderLoginView(contentEl, empezar);
}

salirEl.addEventListener("click", async () => {
  // La sesión se cierra en el servidor aunque la red falle al responder: dejar
  // la pantalla de ingreso puesta es lo correcto en los dos casos.
  try {
    await salir();
  } finally {
    state.threadId = null;
    pedirIngreso();
  }
});

// Una sesión vencida puede aparecer en cualquier pantalla, incluso mientras se
// sondea una conciliación en curso.
alPerderLaSesion(() => {
  if (accesoRequerido) pedirIngreso();
});

// ── Bootstrap ─────────────────────────────────────────────────────────────────

async function boot(): Promise<void> {
  let sesion;
  try {
    sesion = await consultarSesion();
  } catch {
    // Sin respuesta del servidor no se puede saber si hace falta ingresar. Se
    // muestra el ingreso: si la instalación no pide credenciales, el primer
    // intento lo va a decir con un mensaje claro.
    accesoRequerido = true;
    pedirIngreso();
    return;
  }

  accesoRequerido = sesion.acceso_requerido;

  if (accesoRequerido && !sesion.usuario) {
    pedirIngreso();
    return;
  }

  await empezar(sesion.usuario);
}

void boot();
